import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { PaymentFrequencyEnum } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import {
  findPreferredMonthlyPaymentPlan,
  getPaymentPlansByType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails/utils';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';

const ENABLED_STATES_FOR_FOOTER_V2 = ['IL', 'TX'];
const ENABLED_STATES_FOR_FLEXIBLE_FOOTER = ['GA'];

type MonthlyPlan = {
  downPaymentAmount: string;
  totalPayments: number;
  installmentAmount: string;
  totalPremiumAmount: string;
  totalAmount: string;
};

type InFullPlan = {
  totalAmount: string;
  detailsAmount: string;
  discountPercentage: string;
};

type BasePaymentConfig = {
  state: string;
  isFullPlanOnly: boolean;
  showPaymentPlanDetails: boolean;
};

type FlexibleBasePaymentConfig = {
  plan: 'flexible';
};

type PaymentConfigMonthly = BasePaymentConfig & {
  plan: 'monthly';
  monthlyPlan: MonthlyPlan;
  inFullPlan?: never; // Ensures no inFullPlan is included
};

type PaymentConfigFull = BasePaymentConfig & {
  plan: 'full';
  inFullPlan: InFullPlan;
  monthlyPlan?: never; // Ensures no monthlyPlan is included
};

type FlexiblePaymentConfigFull = FlexibleBasePaymentConfig & {
  totalPremiumAmount: string;
};

type FlexiblePaymentConfigMonthly = FlexibleBasePaymentConfig & {
  installmentAmount: string;
  totalPremiumAmount: string;
  frequencyLabel: string;
};

type FlexiblePaymentConfigWeekly = FlexibleBasePaymentConfig & {
  installmentAmount: string;
  totalPremiumAmount: string;
  frequencyLabel: string;
};

type FlexiblePaymentConfigBiWeekly = FlexibleBasePaymentConfig & {
  installmentAmount: string;
  totalPremiumAmount: string;
  frequencyLabel: string;
};

type FlexiblePaymentConfigSemiMonthly = FlexibleBasePaymentConfig & {
  installmentAmount: string;
  totalPremiumAmount: string;
  frequencyLabel: string;
};

type RateConfigType = {
  details: GetDetailsResponseType;
  rate: RateType | null;
};

type FooterVersion = 'v1' | 'v2' | 'flexible';

export type PaymentConfigType =
  | PaymentConfigMonthly
  | PaymentConfigFull
  | FlexiblePaymentConfigMonthly
  | FlexiblePaymentConfigWeekly
  | FlexiblePaymentConfigFull;

const resolveFooterVersion = (state: string): FooterVersion => {
  if (ENABLED_STATES_FOR_FLEXIBLE_FOOTER.includes(state)) return 'flexible';
  if (ENABLED_STATES_FOR_FOOTER_V2.includes(state)) return 'v2';
  return 'v1';
};

const getFooterHeaders = (
  version: FooterVersion,
  options: { hasFees: boolean; totalTaxAmount?: string; isFullPlan: boolean }
) => {
  const { hasFees, totalTaxAmount, isFullPlan } = options;

  switch (version) {
    case 'flexible':
      return {
        title: isFullPlan
          ? 'Total 6-Month Term Cost'
          : 'Estimated 6-Month policy cost',
        subtitle: hasFees
          ? 'Additional taxes and fees may apply at purchase'
          : isFullPlan
          ? 'Estimates shown. Payment plan options may vary at purchase'
          : '',
      };
    case 'v2':
      return {
        title: 'Total 6-Month Term Cost',
        subtitle: 'Estimates shown. Payment plan options may vary at purchase',
      };
    case 'v1':
    default:
      return {
        title: `Estimated 6-Month Premium${totalTaxAmount ? ' + Taxes' : ''}`,
        subtitle: hasFees ? 'Additional fees may apply at purchase' : '',
      };
  }
};

const useRateConfig = (props: RateConfigType) => {
  const { details, rate } = props;
  const defaultRateConfig = {
    isRateRenderable: false,
    rateHeaders: { title: '', subtitle: '' },
    getPaymentConfig: () => undefined,
  };

  if (!details || !rate) {
    return defaultRateConfig;
  }

  const state = details.address[details.principalInsuredId].state.value;
  const { paymentPlans } = rate.paymentDetails.attributes || {};

  const hasPaymentPlansAvailable = paymentPlans && paymentPlans?.length > 0;

  if (!hasPaymentPlansAvailable) {
    return defaultRateConfig;
  }

  const monthlyPaymentPlans = getPaymentPlansByType(paymentPlans, 'monthly');
  const monthlyPlan = findPreferredMonthlyPaymentPlan(monthlyPaymentPlans);

  const [inFullPlan] = getPaymentPlansByType(paymentPlans, 'in_full');
  const [fullPay] = getPaymentPlansByType(paymentPlans, 'full_pay');

  const [weeklyPlan] = getPaymentPlansByType(paymentPlans, 'weekly');
  const [biWeeklyPlan] = getPaymentPlansByType(paymentPlans, 'bi_weekly');
  const [semiMonthlyPlan] = getPaymentPlansByType(paymentPlans, 'semi_monthly');

  const hasFees = paymentPlans.some(plan => plan.totalFeeAmount !== null);

  const footerVersion = resolveFooterVersion(state);
  const { title, subtitle } = getFooterHeaders(footerVersion, {
    hasFees,
    totalTaxAmount: monthlyPlan?.totalTaxAmount || '',
    isFullPlan: Boolean(inFullPlan || fullPay),
  });

  const getPaymentConfig = (
    paymentFrequency: PaymentFrequencyEnum
  ): PaymentConfigType | undefined => {
    if (paymentFrequency === 'monthly' && monthlyPlan) {
      if (monthlyPlan.paymentPlanEnum === 'flexible') {
        const config: FlexiblePaymentConfigMonthly = {
          plan: 'flexible',
          frequencyLabel: 'Monthly',
          installmentAmount: monthlyPlan.installmentAmount,
          totalPremiumAmount: monthlyPlan.totalPremiumAndTaxAmount,
        };

        return config;
      }

      const hasPayInFullDiscount = inFullPlan?.discounts.some(
        discount => discount.type === 'paymentFrequencyDiscount'
      );

      const monthlyConfigV1 = {
        downPaymentAmount: monthlyPlan.monthlyAvgPremiumAndTaxAmount,
        showPaymentPlanDetails: !!hasPayInFullDiscount,
      };

      const monthlyConfigV2 = {
        downPaymentAmount: monthlyPlan.downPaymentAmount,
        showPaymentPlanDetails: true,
      };

      const monthlyConfig =
        footerVersion === 'v2' ? monthlyConfigV2 : monthlyConfigV1;

      return {
        state,
        plan: 'monthly',
        showPaymentPlanDetails: monthlyConfig.showPaymentPlanDetails,
        isFullPlanOnly: false,
        monthlyPlan: {
          downPaymentAmount: monthlyConfig.downPaymentAmount,
          totalPayments: monthlyPlan.paymentSchedule.length - 1, // Exclude down payment
          installmentAmount: monthlyPlan.installmentAmount,
          totalPremiumAmount: monthlyPlan?.totalPremiumAndTaxAmount || '',
          totalAmount: monthlyPlan.totalAmount,
        },
      };
    }

    // inFull is a non-flexible/standard plan
    if (paymentFrequency === 'in_full' && inFullPlan) {
      const hasPayInFullDiscount = inFullPlan.discounts.some(
        discount => discount.type === 'paymentFrequencyDiscount'
      );

      const inFullConfigV1 = {
        showPaymentPlanDetails: !!hasPayInFullDiscount,
        totalAmount: inFullPlan.totalPremiumAndTaxAmount,
      };

      const inFullConfigV2 = {
        showPaymentPlanDetails: true,
        totalAmount: monthlyPlan?.totalAmount || inFullPlan.totalAmount,
      };

      const inFullConfig =
        footerVersion === 'v2' ? inFullConfigV2 : inFullConfigV1;

      const inFullPlanDiscount = inFullPlan.discounts.find(
        discount =>
          discount.type === 'paymentFrequencyDiscount' &&
          discount.amount !== null &&
          discount.amount !== 0
      );

      const isFullPlanOnly = Boolean((inFullPlan || fullPay) && !monthlyPlan);

      return {
        state,
        plan: 'full',
        showPaymentPlanDetails: inFullConfig.showPaymentPlanDetails,
        isFullPlanOnly,
        inFullPlan: {
          totalAmount: inFullConfig.totalAmount,
          detailsAmount: inFullPlan.totalAmount,
          discountPercentage: inFullPlanDiscount?.amount + '%',
        },
      };
    }

    // full_pay is part of the flexible plans
    if (paymentFrequency === 'full_pay' && fullPay) {
      const config: FlexiblePaymentConfigFull = {
        plan: 'flexible',
        totalPremiumAmount: fullPay.totalPremiumAndTaxAmount,
      };

      return config;
    }

    if (
      paymentFrequency === 'weekly' &&
      weeklyPlan &&
      weeklyPlan.paymentPlanEnum === 'flexible'
    ) {
      const config: FlexiblePaymentConfigWeekly = {
        plan: 'flexible',
        frequencyLabel: 'Weekly',
        installmentAmount: weeklyPlan.installmentAmount,
        totalPremiumAmount: weeklyPlan.totalPremiumAndTaxAmount,
      };

      return config;
    }

    if (
      paymentFrequency === 'bi_weekly' &&
      biWeeklyPlan &&
      biWeeklyPlan.paymentPlanEnum === 'flexible'
    ) {
      const config: FlexiblePaymentConfigBiWeekly = {
        plan: 'flexible',
        frequencyLabel: 'Every two weeks',
        installmentAmount: biWeeklyPlan.installmentAmount,
        totalPremiumAmount: biWeeklyPlan.totalPremiumAndTaxAmount,
      };

      return config;
    }

    if (
      paymentFrequency === 'semi_monthly' &&
      semiMonthlyPlan &&
      semiMonthlyPlan.paymentPlanEnum === 'flexible'
    ) {
      const config: FlexiblePaymentConfigSemiMonthly = {
        plan: 'flexible',
        frequencyLabel: 'Twice a month',
        installmentAmount: semiMonthlyPlan.installmentAmount,
        totalPremiumAmount: semiMonthlyPlan.totalPremiumAndTaxAmount,
      };

      return config;
    }

    return undefined;
  };

  return {
    isRateRenderable: hasPaymentPlansAvailable,
    rateHeaders: { title, subtitle },
    getPaymentConfig,
  };
};

export default useRateConfig;
